import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, type FieldSpec, type LegacyRow } from './fieldMap';

// clip_types table; LibraryMapping.ToDto(ClipType) in lureo-backend Data/Api/V1/Library/LibraryDtos.cs
export type ClipTypeDto = components['schemas']['ClipTypeDto'];
export type ClipSourceRequest = components['schemas']['ClipSourceRequest'];

const spec = {
  uniqueId: 'clip_type_id',
  assetTypeUniqueId: 'asset_type_id',
  // The scene the clip was imported in
  sceneUniqueId: 'scene_instance_id',
  sourceName: 'clip_type_source_name',
  displayName: 'clip_type_display_name',
  duration: 'clip_type_duration',
  displayOn: 'clip_type_display_on',
  category: 'clip_type_category',
  // v1 returns an object ({} when the stored value is not one)
  scope: { legacy: 'clip_type_scope', as: 'jsonText' },
  settings: { legacy: 'clip_type_settings', as: 'jsonText' },
} as const satisfies FieldSpec<ClipTypeDto>;

const map = fieldMap<ClipTypeDto, typeof spec>('Clip type', spec);

export type LegacyClipType = LegacyRow<typeof spec, ClipTypeDto>;

/** updateClipType accepts these. */
export const CLIP_TYPE_PATCH_FIELDS = ['sourceName', 'displayName', 'duration', 'displayOn', 'category', 'scope', 'settings'] as const;

export const clipTypeToLegacy = (dto: ClipTypeDto): LegacyClipType => map.toLegacy(dto);

/**
 * One clip of upsertClipTypes (POST /api/v1/clip-types/batch) from a legacy clip_types row.
 * asset_type_id and scene_instance_id belong on the request, not the clip, so they are skipped.
 * clip_type_id is refused: the server keys clips by (assetTypeId, sourceName) and returns their ids.
 */
export function clipTypeUpsertClip(row: Partial<LegacyClipType>): ClipSourceRequest {
  return map.toBody(row, CLIP_TYPE_PATCH_FIELDS, ['asset_type_id', 'scene_instance_id']);
}

/** updateClipType body from a legacy partial update. */
export const clipTypePatchBody = (changes: Partial<LegacyClipType>) => map.toBody(changes, CLIP_TYPE_PATCH_FIELDS);
